/**
 * Centralized Query Keys for TanStack Query
 * 
 * This file defines all query keys used throughout the application.
 * Using centralized query keys ensures:
 * - Type safety
 * - Consistency
 * - Easy refactoring
 * - Better IDE autocomplete
 * 
 * Convention:
 * - All keys are defined as `as const` for type safety
 * - Keys follow the pattern: ['resource', ...params]
 * - For list queries: ['resource']
 * - For detail queries: ['resource', id]
 * - For filtered queries: ['resource', { filter1, filter2 }]
 */

type QueryKeyParams = Record<string, unknown>;

/**
 * Authentication & User Profile
 */
export const QUERY_KEYS = {
  /**
   * Authenticated user profile
   * Query Key: ['auth', 'me']
   */
  auth: {
    me: ['auth', 'me'] as const,
  },

  /**
   * Menu & Permissions
   */
  menu: {
    /**
     * User's menu items (filtered by permissions)
     * Query Key: ['menu', 'my-menu']
     */
    myMenu: ['menu', 'my-menu'] as const,
  },

  overview: {
    /** Per-user cache partition (role/override đổi → refetch sau TTL hoặc focus) */
    runtime: (userId: string) => ['overview', 'runtime', userId] as const,
    adminTemplates: ['overview', 'admin-templates'] as const,
    adminRoleMappings: ['overview', 'admin-role-mappings'] as const,
  },

  /**
   * Permissions
   */
  permissions: {
    /**
     * Current user's permissions (with pathname for route-specific permissions)
     * Query Key: ['my-permissions', pathname]
     * Note: pathname is dynamic, so this is a factory function
     */
    myPermissions: (pathname: string) => ['my-permissions', pathname] as const,
  },

  /**
   * Settings
   */
  settings: {
    /**
     * Theme settings (system-wide)
     * Query Key: ['theme', 'settings']
     */
    theme: ['theme', 'settings'] as const,
    /**
     * User theme preference (per-user) — IW3.17.C
     * Query Key: ['user-theme', userId]
     */
    userTheme: (userId: string) => ['user-theme', userId] as const,
    /**
     * Security settings (auto logout, etc.)
     * Query Key: ['settings', 'security']
     */
    security: ['settings', 'security'] as const,
    /**
     * Assignment eligibility settings (role-based filter for main/backup)
     * Query Key: ['settings', 'assignment-eligibility']
     */
    assignmentEligibility: ['settings', 'assignment-eligibility'] as const,
    /**
     * Assignment eligibility role options (distinct users.position)
     * Query Key: ['settings', 'assignment-eligibility', 'roles']
     */
    assignmentEligibilityRoleOptions: ['settings', 'assignment-eligibility', 'roles'] as const,
    /**
     * Assignment eligibility impact preview (POST body hash)
     * Query Key: ['settings', 'assignment-eligibility', 'impact', hash]
     */
    assignmentEligibilityImpact: (hash: string) => ['settings', 'assignment-eligibility', 'impact', hash] as const,
  },

  /**
   * Branches
   */
  branches: {
    /**
     * All branches list
     * Query Key: ['branches']
     */
    all: ['branches'] as const,
  },

  /**
   * Roles
   */
  roles: {
    /**
     * All roles list
     * Query Key: ['roles']
     */
    all: ['roles'] as const,
  },

  /**
   * Users
   */
  users: {
    /**
     * All users list (no params)
     * Query Key: ['users']
     */
    all: ['users'] as const,
    /**
     * Users directory (minimal data for assignment/selection)
     * Query Key: ['users', 'directory']
     */
    directory: ['users', 'directory'] as const,
    /**
     * Users lookup (minimal: id, name, email) for Delegation Manager / Roles — IW3.16.D1
     * Query Key: ['users', 'lookup', search?]
     */
    lookup: (search?: string) => (search ? ['users', 'lookup', search] as const : ['users', 'lookup'] as const),

    /**
     * Users with query parameters
     * Query Key: ['users', { limit?, search?, role?, department?, page? }]
     * Note: This is a factory function to handle params
     */
    list: (params?: {
      limit?: number;
      search?: string;
      role?: string;
      department?: string;
      page?: number;
    }) => {
      if (!params || Object.keys(params).length === 0) {
        return ['users'] as const;
      }
      // Sort params for consistent query key
      const sortedParams = Object.keys(params)
        .sort()
        .reduce((acc, key) => {
          if (params[key as keyof typeof params] !== undefined) {
            acc[key] = params[key as keyof typeof params];
          }
          return acc;
        }, {} as QueryKeyParams);
      return ['users', sortedParams] as const;
    },
  },

  /**
   * Projects
   */
  projects: {
    /**
     * All projects list (no params)
     * Query Key: ['projects']
     */
    all: ['projects'] as const,
    
    /**
     * Projects with query parameters (list mode: page, pageSize, filters)
     * Query Key: ['projects', { page?, pageSize?, search?, status?, branch?, manager?, rank?, version? }]
     */
    list: (params?: {
      page?: number;
      pageSize?: number;
      search?: string;
      status?: string;
      branch?: string;
      rank?: string;
      manager?: string;
      version?: string;
    }) => {
      if (!params || Object.keys(params).length === 0) {
        return ['projects'] as const;
      }
      // Sort params for consistent query key
      const sortedParams = Object.keys(params)
        .sort()
        .reduce((acc, key) => {
          if (params[key as keyof typeof params] !== undefined) {
            acc[key] = params[key as keyof typeof params];
          }
          return acc;
        }, {} as QueryKeyParams);
      return ['projects', sortedParams] as const;
    },
  },

  /**
   * Work Reports
   */
  workReports: {
    /**
     * All work reports list (no params)
     * Query Key: ['work-reports']
     */
    all: ['work-reports'] as const,
    
    /**
     * Work reports with query parameters
     * Query Key: ['work-reports', { q?, status?, from?, to?, assigneeId?, projectId?, jobTypeId?, page?, pageSize? }]
     * Note: This is a factory function to handle params
     */
    list: (params?: {
      q?: string;
      status?: string;
      from?: string;
      to?: string;
      assigneeId?: string;
      projectId?: string;
      jobTypeId?: string;
      page?: number;
      pageSize?: number;
    }) => {
      if (!params || Object.keys(params).length === 0) {
        return ['work-reports'] as const;
      }
      // Sort params for consistent query key
      const sortedParams = Object.keys(params)
        .sort()
        .reduce((acc, key) => {
          if (params[key as keyof typeof params] !== undefined) {
            acc[key] = params[key as keyof typeof params];
          }
          return acc;
        }, {} as QueryKeyParams);
      return ['work-reports', sortedParams] as const;
    },
    
    /**
     * Single work report by ID
     * Query Key: ['work-reports', id]
     */
    detail: (id: string) => ['work-reports', id] as const,
  },

  /**
   * Work Report Job Types
   */
  workReportJobTypes: {
    /**
     * All job types list (no params)
     * Query Key: ['work-report-job-types']
     */
    all: ['work-report-job-types'] as const,
    
    /**
     * Job types with query parameters
     * Query Key: ['work-report-job-types', { q?, active? }]
     * Note: This is a factory function to handle params
     */
    list: (params?: {
      q?: string;
      active?: boolean;
    }) => {
      if (!params || Object.keys(params).length === 0) {
        return ['work-report-job-types'] as const;
      }
      // Sort params for consistent query key
      const sortedParams = Object.keys(params)
        .sort()
        .reduce((acc, key) => {
          if (params[key as keyof typeof params] !== undefined) {
            acc[key] = params[key as keyof typeof params];
          }
          return acc;
        }, {} as QueryKeyParams);
      return ['work-report-job-types', sortedParams] as const;
    },
  },

  /**
   * Incidents Config (Admin)
   */
  incidentsConfig: {
    /**
     * Service tiers list (admin - includes inactive)
     * Query Key: ['incidents-config', 'service-tiers']
     */
    serviceTiers: ['incidents-config', 'service-tiers'] as const,
    /**
     * Service tiers list with pagination (admin)
     * Query Key: ['incidents-config', 'service-tiers', params]
     */
    serviceTiersList: (params?: { page?: number; pageSize?: number }) => {
      if (!params || Object.keys(params).length === 0) {
        return ['incidents-config', 'service-tiers'] as const;
      }
      const sortedParams = Object.keys(params)
        .sort()
        .reduce((acc, key) => {
          if (params[key as keyof typeof params] !== undefined) {
            acc[key] = params[key as keyof typeof params];
          }
          return acc;
        }, {} as QueryKeyParams);
      return ['incidents-config', 'service-tiers', sortedParams] as const;
    },
    /**
     * Impact types list (admin - includes inactive)
     * Query Key: ['incidents-config', 'impact-types']
     */
    impactTypes: ['incidents-config', 'impact-types'] as const,
    /**
     * Impact types list with pagination (admin)
     * Query Key: ['incidents-config', 'impact-types', params]
     */
    impactTypesList: (params?: { page?: number; pageSize?: number }) => {
      if (!params || Object.keys(params).length === 0) {
        return ['incidents-config', 'impact-types'] as const;
      }
      const sortedParams = Object.keys(params)
        .sort()
        .reduce((acc, key) => {
          if (params[key as keyof typeof params] !== undefined) {
            acc[key] = params[key as keyof typeof params];
          }
          return acc;
        }, {} as QueryKeyParams);
      return ['incidents-config', 'impact-types', sortedParams] as const;
    },
    /**
     * Impact scopes list (admin - includes inactive)
     * Query Key: ['incidents-config', 'impact-scopes']
     */
    impactScopes: ['incidents-config', 'impact-scopes'] as const,
    /**
     * Impact scopes list with pagination (admin)
     * Query Key: ['incidents-config', 'impact-scopes', params]
     */
    impactScopesList: (params?: { page?: number; pageSize?: number }) => {
      if (!params || Object.keys(params).length === 0) {
        return ['incidents-config', 'impact-scopes'] as const;
      }
      const sortedParams = Object.keys(params)
        .sort()
        .reduce((acc, key) => {
          if (params[key as keyof typeof params] !== undefined) {
            acc[key] = params[key as keyof typeof params];
          }
          return acc;
        }, {} as QueryKeyParams);
      return ['incidents-config', 'impact-scopes', sortedParams] as const;
    },
    /**
     * KPI policies list (admin - includes inactive)
     * Query Key: ['incidents-config', 'kpi-policies']
     */
    kpiPolicies: ['incidents-config', 'kpi-policies'] as const,
    /**
     * KPI policies list with pagination (admin)
     * Query Key: ['incidents-config', 'kpi-policies', params]
     */
    kpiPoliciesList: (params?: { page?: number; pageSize?: number }) => {
      if (!params || Object.keys(params).length === 0) {
        return ['incidents-config', 'kpi-policies'] as const;
      }
      const sortedParams = Object.keys(params)
        .sort()
        .reduce((acc, key) => {
          if (params[key as keyof typeof params] !== undefined) {
            acc[key] = params[key as keyof typeof params];
          }
          return acc;
        }, {} as QueryKeyParams);
      return ['incidents-config', 'kpi-policies', sortedParams] as const;
    },
    /**
     * Severity scoring configs list (admin - includes inactive)
     * Query Key: ['incidents-config', 'severity-configs']
     */
    severityConfigs: ['incidents-config', 'severity-configs'] as const,
    /**
     * Severity scoring rules list for a config (admin)
     * Query Key: ['incidents-config', 'severity-rules', configId]
     */
    severityRules: (configId: string) => ['incidents-config', 'severity-rules', configId] as const,
    /**
     * Severity levels list (admin)
     * Query Key: ['incidents-config', 'severity-levels']
     */
    severityLevels: ['incidents-config', 'severity-levels'] as const,
    /**
     * Conditional rules list (admin)
     * Query Key: ['incidents-config', 'conditional-rules', params]
     */
    conditionalRules: (params: { page?: number; pageSize?: number; enabled?: string; severity?: string }) =>
      ['incidents-config', 'conditional-rules', params] as const,
  },

  /**
   * Incidents
   */
  incidents: {
    /**
     * All incidents list (no params)
     * Query Key: ['incidents']
     */
    all: ['incidents'] as const,
    
    /**
     * Incidents with query parameters
     * Query Key: ['incidents', { q?, status?, severity_final?, project_id?, owner_id?, service_tier?, from?, to?, page?, pageSize? }]
     * Note: This is a factory function to handle params
     */
    list: (params?: {
      q?: string;
      status?: string;
      severity_final?: string;
      project_id?: string;
      owner_id?: string;
      service_tier?: string;
      from?: string;
      to?: string;
      page?: number;
      pageSize?: number;
    }) => {
      if (!params || Object.keys(params).length === 0) {
        return ['incidents'] as const;
      }
      // Sort params for consistent query key
      const sortedParams = Object.keys(params)
        .sort()
        .reduce((acc, key) => {
          if (params[key as keyof typeof params] !== undefined) {
            acc[key] = params[key as keyof typeof params];
          }
          return acc;
        }, {} as QueryKeyParams);
      return ['incidents', sortedParams] as const;
    },
    
    /**
     * Single incident by ID
     * Query Key: ['incidents', id]
     */
    detail: (id: string) => ['incidents', id] as const,
    
    /**
     * Incident config options (service tiers, impact types, impact scopes)
     * Query Key: ['incidents', 'config', 'options']
     */
    options: () => ['incidents', 'config', 'options'] as const,
  },

  /**
   * Incident Steps
   */
  incidentSteps: {
    /**
     * Steps for an incident
     * Query Key: ['incidents', incidentId, 'steps']
     */
    list: (incidentId: string) => ['incidents', incidentId, 'steps'] as const,
  },

  /**
   * Admin System Sizing configuration (S6) — distinct from User Sizing (S7) keys.
   */
  sizingAdmin: {
    /** Query Key: ['sizing-admin', 'templates'] */
    templates: ['sizing-admin', 'templates'] as const,
    /** Query Key: ['sizing-admin', 'templates', id] */
    template: (id: string) => ['sizing-admin', 'templates', id] as const,
    /** Query Key: ['sizing-admin', 'templates', id, 'draft'] */
    draft: (id: string) => ['sizing-admin', 'templates', id, 'draft'] as const,
  },

  /**
   * User System Sizing execution (S7) — never collide with sizingAdmin.*.
   */
  sizingUser: {
    /** Query Key: ['sizing-user', 'active-templates'] */
    activeTemplates: ['sizing-user', 'active-templates'] as const,
    /** Query Key: ['sizing-user', 'history', { page, pageSize, search }] */
    history: (params: { page: number; pageSize: number; search: string }) =>
      ['sizing-user', 'history', params] as const,
    /** Query Key: ['sizing-user', 'detail', id] */
    detail: (id: string) => ['sizing-user', 'detail', id] as const,
  },

  /**
   * Incident Step Templates
   */
  incidentStepTemplates: {
    /**
     * All templates list (no params)
     * Query Key: ['incident-step-templates']
     */
    all: ['incident-step-templates'] as const,
    
    /**
     * Template items by template ID
     * Query Key: ['incident-step-templates', templateId, 'items']
     */
    items: (templateId: string) => ['incident-step-templates', templateId, 'items'] as const,
  },
} as const;

/**
 * Type helpers for query keys
 */
export type QueryKey = typeof QUERY_KEYS[keyof typeof QUERY_KEYS][keyof typeof QUERY_KEYS[keyof typeof QUERY_KEYS]];

